import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ChevronLeft,
  Settings,
  Medal,
  Menu,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../ui/logo.png';
import { FaChalkboardTeacher } from 'react-icons/fa';
import { MdEventNote } from 'react-icons/md';
import { IoCardOutline } from 'react-icons/io5';
import { BsGraphUpArrow } from 'react-icons/bs';
import useParent from '../../hooks/useParent';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/parent/dashboard' },
  { icon: Medal, label: 'Attendance', href: '/parent/attendance' },
  { icon: FaChalkboardTeacher, label: 'Teachers', href: '/parent/teachers' },
  { icon: MdEventNote, label: 'Events', href: '/parent/events' },
  { icon: IoCardOutline, label: 'Payment', href: '/parent/payment' },
  {
    icon: BsGraphUpArrow,
    label: 'Parent’s Guide',
    href: '/parent/parent-guide',
  },
  {
    icon: Settings,
    label: 'Settings',
    href: '/parent/settings',
  },
];

export default function ParentSidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { currentChild } = useParent();

  // Check if current path matches menu item path or if it's a subpath
  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div>
      <motion.div
        className="relative min-h-[140lvh] h-full border-r border-gray-100 bg-white md:block hidden"
        initial={false}
        animate={{
          width: isCollapsed ? 88 : 280,
          transition: { duration: 0.3, ease: 'easeInOut' },
        }}
      >
        {/* Logo Section */}
        <div className="flex h-20 items-center px-6">
          <Link to="/" className="flex items-center gap-4">
            <img src={logo} alt="Logo" className="h-32 w-32 object-contain" />
            {/* <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                className="text-xl font-semibold"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                Campus Manager
              </motion.span>
            )}
          </AnimatePresence> */}
          </Link>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-transform hover:scale-105 focus:outline-none"
        >
          <motion.div
            initial={false}
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </motion.div>
        </button>

        {/* Navigation */}
        <nav className="mt-6 px-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const isActiveRoute = isActive(item.href);
              const isSettings = item.label === 'Settings';
              const shouldDisable = !currentChild && !isSettings;

              return (
                <li key={item.label} className="relative">
                  {shouldDisable ? (
                    // Render as a disabled span
                    <span className="group relative flex items-center gap-4 rounded-lg px-4 py-3 text-base font-medium text-gray-400 cursor-not-allowed bg-gray-100">
                      <item.icon className="h-6 w-6 flex-shrink-0 text-gray-400" />
                      {!isCollapsed && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </span>
                  ) : (
                    // Render as a clickable link
                    <Link
                      to={item.href}
                      state={{
                        label: isSettings ? 'Account Information' : item.label,
                      }}
                      className={`group relative flex items-center gap-4 rounded-lg px-4 py-3 text-base font-medium transition-all duration-200
            ${
              isActiveRoute
                ? 'bg-purple-50 text-purple-700'
                : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
            }`}
                    >
                      <item.icon
                        className={`h-6 w-6 flex-shrink-0 transition-colors
              ${
                isActiveRoute
                  ? 'text-purple-700'
                  : 'text-gray-500 group-hover:text-purple-600'
              }`}
                      />
                      {!isCollapsed && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </motion.div>

      {/* Mobile Nav */}
      <div className="block md:hidden absolute top-5 left-5 z-50">
        <Menu color="#8147E7" onClick={() => setIsMobileOpen(!isMobileOpen)} />
      </div>
      <AnimatePresence>
        <motion.div
          className="bg-white w-[50vw] h-[100vh] absolute top-0 left-0 z-50"
          initial={{ x: '-100%' }}
          animate={{ x: isMobileOpen ? 0 : '-100%' }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {/* Logo Section */}
          <div className="flex h-20 items-center px-6 justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img src={logo} alt="Logo" className="h-20 w-20 object-contain" />
            </Link>
            <X color="#c85050" onClick={() => setIsMobileOpen(!isMobileOpen)} />
          </div>

          {/* Navigation */}
          <nav>
            <ul>
              {menuItems.map((item) => {
                const isActiveRoute = isActive(item.href);
                const isSettings = item.label === 'Settings';
                const shouldDisable = !currentChild && !isSettings;

                return (
                  <li key={item.label} className="relative">
                    {shouldDisable ? (
                      // Render as a disabled span
                      <span className="group relative flex items-center gap-4 rounded-lg px-4 py-3 text-base font-medium text-gray-400 cursor-not-allowed bg-gray-100">
                        <item.icon className="h-6 w-6 flex-shrink-0 text-gray-400" />
                        {!isCollapsed && (
                          <motion.span
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </span>
                    ) : (
                      // Render as a clickable link
                      <Link
                        to={item.href}
                        state={{
                          label: isSettings
                            ? 'Account Information'
                            : item.label,
                        }}
                        className={`group relative flex items-center gap-4 rounded-lg px-4 py-3 text-base font-medium transition-all duration-200
            ${
              isActiveRoute
                ? 'bg-purple-50 text-purple-700'
                : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
            }`}
                      >
                        <item.icon
                          className={`h-6 w-6 flex-shrink-0 transition-colors
              ${
                isActiveRoute
                  ? 'text-purple-700'
                  : 'text-gray-500 group-hover:text-purple-600'
              }`}
                        />
                        {!isCollapsed && (
                          <motion.span
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
